import { memo } from 'react';
import cn from 'classnames';

/* Store */
import useAddSkuStore from '@scenes/AddSkuPage/store/store.addsku';

/* Components */
import { Checkbox, CheckboxGroup, ErrorMessage } from 'ht-styleguide';
import { WarningHintIcon } from '@components/UI/Messages';
import CheckboxQuantitySelector from './CheckboxQuantitySelector';
import { isPartnerSubsidizedAddOn } from '../utils';

/* Utils */
import { isPlainObjectWithKeys } from '@utils/object';

/* styles */
import styles from '../prequestioncheckbox.module.scss';

/* types */
import { AdjAmountFormatted, Answer, Question, Sku } from 'types/sku.types';
import { CheckBoxValue } from 'types/form.types';

type PreQuestionCheckboxProps = {
  question: Question;
  sku: Sku;
  value?: CheckBoxValue;
  error?: string;
  onChange: BaseAnyFunction;
  onQuantityChange: BaseAnyFunction;
};

const PreQuestionCheckbox = (props: PreQuestionCheckboxProps) => {
  /* Store */
  const updateAddSkuQuantity = useAddSkuStore(s => s.actions.updateAddSkuQuantity);

  const { question, value: asValue = [], onChange, sku, error } = props;
  const value = asValue || [];
  const values = (Array.isArray(value) && value.map(v => v.id)) || [];
  const checkboxStyles = cn(styles.preQuestionCheckbox, {
    [styles.preQuestionCheckboxError]: error,
  });
  const answerStyles = cn(styles.checkboxWrapper, styles.answer);
  const hasHint = Boolean(question.hint);
  const onQuantityChange = async (answer: Answer, quantity: number) => {
    await updateAddSkuQuantity({ question, id: answer.id, quantity });
  };

  const renderAnswer = ({ answer, value: cbv, sku: qaSku }: { answer: Answer; value: CheckBoxValue; sku: Sku }) => {
    const checkboxClass = cn({
      [styles.check]: !answer.hasQuantity,
    });

    let label = answer.text;
    if (answer.adjAmountFormatted) {
      const partnerText = isPartnerSubsidizedAddOn({ answer, sku }) ? ` Paid by ${sku?.partner?.name ?? 'Partner'}` : '';
      const answerAdjAmountFormatted = answer.adjAmountFormatted;
      const amount = isPlainObjectWithKeys(answerAdjAmountFormatted)
        ? (answerAdjAmountFormatted as AdjAmountFormatted)?.formatted || '(Error)'
        : answerAdjAmountFormatted;

      label += ` (+${amount}${partnerText})`;
    }
    const selectedValue = cbv.find(v => v.id === answer.id);
    /* have to manage here for ts acceptance */
    const CQS = () =>
      (answer.hasQuantity && selectedValue && (
        <CheckboxQuantitySelector answer={answer} selectedValue={selectedValue} onChange={onQuantityChange} />
      )) ||
      null;

    return (
      <div key={answer.id}>
        <Checkbox value={answer.id} label={label} classes={checkboxClass} dataTestId={`qa-checkbox-answer-${answer.id}`}>
          <CQS />
          <WarningHintIcon content={answer.warning} hintIconStyles={styles.hintAdjust} />
        </Checkbox>
        <div className="clear" />
      </div>
    );
  };

  const labelProps = hasHint
    ? {
        labelComponent: (
          <div className="flex alignItemsCenter justifyContentSpaceBetween">
            <span>{question.textDirect} </span>
            <WarningHintIcon content={question.hint} hintIconStyles={styles.hintAdjustCheckbox} />
          </div>
        ),
      }
    : { label: question.textDirect };

  return (
    <div className={checkboxStyles}>
      <div className={answerStyles}>
        <CheckboxGroup value={values} {...labelProps} onChange={onChange}>
          {question.answers.map(a => (
            <div key={a.id}>{renderAnswer({ answer: a, value, sku })}</div>
          ))}
        </CheckboxGroup>
        <ErrorMessage error={error} />
      </div>
    </div>
  );
};

export default memo(PreQuestionCheckbox);
